import { ButtonGroup, TextButton } from '@oneaudi/unified-web-components';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { IncentivesSection } from '~/app/compositions/content/components/incentives-section';
import { PriceSection } from '~/app/compositions/content/components/price-section';
import { SubtotalSection } from '~/app/compositions/content/components/subtotal-section';
import { SummarySection, TopSection } from '~/app/compositions/content/configuration-overview-builder';
import {
	ConfigurationImagesStyle,
	ConfigurationImageWrapperStyle,
	ConfigurationOverviewStyle,
	ConfigurationSectionWrapperStyle,
	ConfigurationSummaryStyle,
} from '~/app/compositions/content/configuration-overview-style';
import { EecLabelSection } from '~/app/compositions/content/eec/eec-label-section';
import { GPSRSection } from '~/app/compositions/content/gpsr';
import { SummaryCtaButton } from '~/app/compositions/content/service-footer/components/user-marketing/main-cta-button';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useMarketUiConfig } from '~/app/market-config/use-market-ui-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { AutoFlowGrid } from '~/app/pages/components/auto-flow-grid';
import { StockCarEnabled, useStockCarDialog } from '~/app/pages/configurator/components/dialogs/stock-car-dialog';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { useSelectedEquipment } from '~/app/pages/summary/hooks/use-selected-equipment';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery, useConfiguredCarTechnicalDataQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useImages } from '~/graphql/hooks/use-images';
import { checkEventActionUrl } from '~/graphql/utils/check-event-action-url';
import GetUrlFromImageSet from '~/graphql/utils/get-from-image-set';
import { modifyImageResolution } from '~/graphql/utils/modify-image-url';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';
import { DATA_TEST_ID } from '~/test/data-test-id';
import { sortIncentives } from '~/utils/incentives';

export function ConfigurationOverview() {
	const { openDialog } = useDialogs();
	const { priceSummary } = useCatalog();
	const { isLoading, carIdentifier } = useConfigurator();
	const vatConfig = useVATConfig();
	const disclaimerText = useAtomValue(state.financingDisclaimerText);
	const { formatCurrency, formatMonthlyRate } = useCurrencyFormatter();
	const { config, formatter } = useMarketConfig();
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);
	const { consumptionAndEmissionSummary } = useMarketUiConfig();
	const { showStockCarDialog } = useStockCarDialog(config.stockCarConfig.geoFilter);
	const { i18n } = useI18n();
	let priceRemark: string | null = null;

	if (config.useStaticPriceDateRemark) {
		priceRemark = config.useSummaryAlternativePriceDateRemark
			? i18n('ngc.summary.alternativeRemark')
			: i18n('ngc.summary.remark');
	} else if (priceSummary?.priceDate?.text) {
		priceRemark = priceSummary?.priceDate?.text;
	}

	const {
		configuration: { featuresAndPackages },
	} = useSelectedEquipment();

	const { imagesByProfileId } = useImages();

	// FIXME: this is only for entertainment and learning purposes a different way how to filter elements from arrays. Enjoy! :)
	// const color = featuresAndPackages.filter((feature) => feature.id === 'Exterior_Colors');
	// const seats = featuresAndPackages.filter((feature) => feature.id === 'cloth:Interior_Tiles');
	const [color, seats] = ['Exterior_Colors', ':Interior_Tiles'].map((id) =>
		featuresAndPackages.find((feature) => feature.id.includes(id)),
	);

	const totalPrice = priceSummary?.parts?.find((part) => part.type === 'total');
	const subtotalPrice = priceSummary?.parts?.find((part) => part.type === 'totalWithoutIncentives');

	const { loading, data: technicalData } = useConfiguredCarTechnicalDataQuery(carIdentifier);

	const incentives = carlineData.carline?.configuredCar.incentives
		? sortIncentives(carlineData.carline?.configuredCar.incentives)
		: [];

	const labelUrl = useMemo(() => {
		if (!technicalData?.configuredCar?.technicalDataV2?.envkv?.urls?.svgUrl) return;
		return technicalData?.configuredCar?.technicalDataV2?.envkv.urls.svgUrl;
	}, [technicalData?.configuredCar?.technicalDataV2?.envkv?.urls?.svgUrl, technicalData]);

	const pdfUrl = useMemo(() => {
		if (!technicalData?.configuredCar?.media?.documents?.envkvLabelPdfUrl) return;
		return technicalData?.configuredCar?.media?.documents?.envkvLabelPdfUrl;
	}, [technicalData?.configuredCar?.media?.documents?.envkvLabelPdfUrl, technicalData]);

	const eecUrl = useMemo(() => {
		if (!technicalData?.configuredCar?.media?.eec?.eecLabelImageUrl) return;
		return technicalData?.configuredCar?.media?.eec?.eecLabelImageUrl;
	}, [technicalData?.configuredCar?.media?.eec?.eecLabelImageUrl, technicalData]);

	const impressionContainerRef = useCallback(
		(node: HTMLElement) => {
			if (node !== null && !isLoading) {
				appTrackingService.summaryImpression({
					node,
					eventName: 'configurator (config overview) - impression',
					componentName: 'configuration overview',
				});
			}
		},
		[isLoading],
	);

	const impressionCTARef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading) {
				const buttonNodes = Array.from(node.childNodes[0].childNodes || []) as HTMLElement[];
				appTrackingService.summaryImpression({
					node,
					eventName: 'configurator (config overview buttons) - impression',
					componentName: 'configuration overview',
					relatedElements: buttonNodes.map((buttonNode, index) => ({
						elementName: 'cta',
						elementValue: `${index + 1}-${buttonNode.innerText}`,
					})),
				});
			}
		},
		[isLoading],
	);

	const trackClickEvent = useCallback((eventName: string, label: string, url?: string) => {
		const buttonsContainerRef = document.querySelector('.buttons-group-cta');
		const buttonNodes = buttonsContainerRef?.getElementsByTagName('button');
		const nodeIndex = Array.from(buttonNodes || [])?.findIndex((buttonNode) => buttonNode.innerText === label);

		if (buttonNodes && nodeIndex !== -1) {
			appTrackingService.summaryClickEvent({
				eventAction: checkEventActionUrl(url || ''),
				eventName: `configurator (configuration overview) - click on ${eventName}`,
				componentName: 'configurator-configuration-overview',
				label,
				targetURL: url,
				elementName: 'button',
				relatedElements: [
					{
						elementName: 'cta',
						elementValue: `${nodeIndex + 1}-${buttonNodes[nodeIndex].id}`,
					},
				],
			});
		}
	}, []);

	return (
		<ConfigurationOverviewStyle
			ref={impressionContainerRef}
			data-testid={DATA_TEST_ID.SUMMARY_CONFIGURATION_OVERVIEW_CONTAINER}>
			<TopSection />
			<ConfigurationSummaryStyle>
				<ConfigurationSectionWrapperStyle>
					<SummarySection />
					{config.enableIncentives && incentives?.length > 0 && (
						<SubtotalSection
							// When no incentive is selected, there is no subtotal price returned from backend.
							subtotal={
								subtotalPrice
									? formatter.formatPrice(subtotalPrice?.price.value)
									: formatter.formatPrice(totalPrice?.price.value)
							}
						/>
					)}
					{config.enableIncentives && <IncentivesSection incentives={incentives} />}
					{config.showPrices && (
						<PriceSection
							totalPrice={formatCurrency(priceSummary?.totalPrice, vatConfig.totalPrice) ?? ''}
							monthlyRate={
								priceSummary?.monthlyRate?.__typename === 'MonthlyRate'
									? formatMonthlyRate(priceSummary?.monthlyRate)
									: null
							}
							remark={priceRemark}
							disclaimer={disclaimerText}
							showSpecialOffers={config.specialOfferUrl.length > 0}
						/>
					)}
					<div ref={impressionCTARef}>
						<ButtonGroup className="buttons-group-cta" orientation="vertical">
							{config.showPrices && (
								<TextButton
									id="price details"
									variant="secondary"
									size={'md'}
									isFullWidth
									onClick={() => {
										openDialog('priceDetails');
										trackClickEvent(
											'open price details layer',
											i18n('ngc.actions.showPriceDetails'),
										);
									}}
									data-testid={DATA_TEST_ID.SUMMARY_PRICE_DETAIL_BUTTON}>
									{i18n('ngc.actions.showPriceDetails')}
								</TextButton>
							)}
							<SummaryCtaButton
								trackClickEvent={trackClickEvent}
								variant="secondary"
								size="md"
								isSecondCta={'false'}
								isFullWidth
							/>
							{StockCarEnabled() && (
								<TextButton
									id="Car Matching"
									className="main-cta-button"
									isFullWidth
									variant="secondary"
									onClick={() => {
										showStockCarDialog();
										trackClickEvent('stock car matching', i18n('ngc.stockcar.trigger'));
									}}
									size={'md'}
									data-testid={DATA_TEST_ID.STOCK_CAR_BUTTON}>
									{i18n('ngc.stockcar.trigger')}
								</TextButton>
							)}
						</ButtonGroup>
					</div>
				</ConfigurationSectionWrapperStyle>
				<ConfigurationImageWrapperStyle>
					<ConfigurationImagesStyle>
						<AutoFlowGrid
							direction="bigImageFirst"
							sources={[
								modifyImageResolution(imagesByProfileId[ImageViewsEnum.EXTERIOR_REAR_3_4], '960'),
								GetUrlFromImageSet(color?.families?.[0]?.items?.[0]?.imageSets, 'outer_tile_button') ||
									GetUrlFromImageSet(
										color?.families?.[0]?.items?.[0]?.imageSets,
										'outer_tile_hd_square',
									) ||
									'//about:blank',
								modifyImageResolution(imagesByProfileId[ImageViewsEnum.EXTERIOR_WHEELS_RIM], '480'),
							]}
						/>
						<AutoFlowGrid
							direction="smallImagesFirst"
							sources={[
								modifyImageResolution(imagesByProfileId[ImageViewsEnum.LIGHTS_FRONT_HEADLIGHTS], '480'),
								GetUrlFromImageSet(
									seats?.families?.[0]?.items?.[0]?.imageSets,
									'interior_tile_button',
								) || '//about:blank',
								modifyImageResolution(
									imagesByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD],
									'960',
								),
							]}
						/>
						{consumptionAndEmissionSummary}
					</ConfigurationImagesStyle>
				</ConfigurationImageWrapperStyle>
			</ConfigurationSummaryStyle>
			{!loading && config.showEECLabel && config.wltpInfo && (
				<EecLabelSection url={labelUrl} pdfURL={pdfUrl} eecURL={eecUrl} />
			)}
			<GPSRSection />
		</ConfigurationOverviewStyle>
	);
}
